<template>
  <div>
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-blue">
        <div class="card-icon">
          <md-icon>public</md-icon>
        </div>
        <h4 class="title">Adventures</h4>
      </md-card-header>
      <md-card-content>
        <md-table v-model="tours" @md-selected="onSelect" class="tour-list">
          <md-table-toolbar>
            <div class="md-toolbar-section-start">
              <md-switch v-model="includeArchives" @change="getTours"
                >Include Archives</md-switch
              >
            </div>

            <div class="md-toolbar-section-end">
              <md-button
                v-if="$allowed('tour.create')"
                class="md-icon-button md-dense md-primary md-round"
                @click="newTour"
              >
                <md-icon>add</md-icon>
                <md-tooltip md-direction="top">Add New Adventure</md-tooltip>
              </md-button>
            </div>
          </md-table-toolbar>
          <md-table-row
            slot="md-table-row"
            slot-scope="{ item }"
            md-selectable="single"
            :class="{ archived: item.archived }"
          >
            <md-table-cell>
              <router-link
                :to="{
                  name: 'Adventure Profile',
                  params: { tour_id: item.id }
                }"
                ><h6>
                  <i
                    v-if="item.type.value == 'event'"
                    class="far fa-calendar text-warning tour-type-icon"
                    ><md-tooltip md-direction="right"
                      >This Adventure is an Event</md-tooltip
                    ></i
                  >
                  {{ item.title }}
                </h6></router-link
              >
            </md-table-cell>
            <md-table-cell>
              <div class="text-right">
                <div class="md-caption tour-type text-primary">
                  {{ item.type.description }}
                </div>
                <md-button
                  v-if="$allowed('tour.manage')"
                  class="md-danger md-just-icon md-simple"
                  @click.stop="deleteTour(item.id)"
                >
                  <md-icon>delete</md-icon>
                  <md-tooltip md-direction="top">Delete Adventure</md-tooltip>
                </md-button>

                <md-button
                  v-if="$allowed('departure.create')"
                  class="md-success md-just-icon md-simple"
                  @click.stop="newDeparture(item)"
                >
                  <md-icon>flight_takeoff</md-icon>
                  <md-tooltip md-direction="top">Add Departure</md-tooltip>
                </md-button>
              </div>
            </md-table-cell>
          </md-table-row>

          <md-table-empty-state
            md-label="Going Somewhere?"
            :md-description="`Let's gets started with a new Adventure.`"
          >
            <md-button class="md-primary md-raised" @click="newTour"
              >Add New Adventure</md-button
            >
          </md-table-empty-state>
        </md-table>
      </md-card-content>
    </md-card>

    <md-dialog :md-active.sync="showTourDialog">
      <md-dialog-title>New Adventure</md-dialog-title>

      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(createTour)" class="mt-4">
          <div class="md-layout">
            <div class="md-layout-item md-size-100">
              <ValidationProvider
                name="Adventure Title"
                rules="required"
                v-slot="{ passed, failed }"
              >
                <md-field
                  :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                >
                  <label>Title</label>
                  <md-input v-model="tour.title" type="text"></md-input>
                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>
            <div class="md-layout-item md-size-100">
              <ValidationProvider
                name="Adventure Type"
                rules="required"
                v-slot="{ passed, failed }"
              >
                <md-field
                  :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                >
                  <label>Type</label>
                  <md-select v-model="tour.type" name="type" id="type">
                    <md-option
                      v-for="item in adventureTypes"
                      :value="item.value"
                      :key="item.key"
                    >
                      {{ item.description }}
                    </md-option>
                  </md-select>
                  <!-- <md-input v-model="tour.title" type="text"></md-input> -->
                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>

            <div class="md-layout-item md-size-100">
              <md-field>
                <label>Partner</label>
                <md-select
                  v-model="tour.supplier"
                  name="supplier"
                  id="supplier"
                >
                  <md-option
                    v-for="item in suppliers"
                    :value="item.id"
                    :key="item.id"
                  >
                    {{ item.company }}
                  </md-option>
                </md-select>
              </md-field>
            </div>
          </div>
          <md-dialog-actions>
            <md-button class="md-primary md-dense" @click="cancelTour"
              >Cancel</md-button
            >
            <md-button type="submit" class="md-primary md-dense"
              >Create</md-button
            >
          </md-dialog-actions>
        </form>
      </ValidationObserver>
    </md-dialog>

    <md-dialog :md-active.sync="showDepartureDialog">
      <md-dialog-title>New Departure</md-dialog-title>

      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(createDeparture)" class="mt-4">
          <div class="md-layout">
            <div class="md-layout-item md-size-100">
              <ValidationProvider
                name="Departure Start Date"
                rules="required"
                v-slot="{ passed, failed }"
              >
                <md-field
                  :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                >
                  <md-datepicker
                    v-model="departure.start_date"
                    md-immediately
                    :md-model-type="String"
                  >
                    <label>Start Date</label>
                  </md-datepicker>
                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>
          </div>
          <md-dialog-actions>
            <md-button class="md-primary md-dense" @click="cancelDeparture"
              >Cancel</md-button
            >
            <md-button type="submit" class="md-primary md-dense"
              >Create</md-button
            >
          </md-dialog-actions>
        </form>
      </ValidationObserver>
    </md-dialog>
  </div>
</template>

<script>
import tourApi from "@/services/api/tour";
import supplierApi from "@/services/api/supplier";
import { mapFields } from "vuex-map-fields";

import _filter from "lodash/filter";

import Swal from "sweetalert2";

import { SlideYDownTransition } from "vue2-transitions";
import { extend } from "vee-validate";
import { required, email } from "vee-validate/dist/rules";

extend("email", email);
extend("required", required);

export default {
  data() {
    return {
      tours: [],
      suppliers: [],
      adventureTypes: [
        {
          key: "tour",
          value: "tour",
          description: "Tour"
        },
        {
          key: "event",
          value: "event",
          description: "Event"
        }
      ],
      showTourDialog: false,
      showDepartureDialog: false,
      tour: {
        type: "tour",
        title: null,
        supplier: null
      },
      departure: {
        tour: null,
        start_date: null
      }
    };
  },

  components: {
    SlideYDownTransition
  },

  methods: {
    onSelect(item) {
      this.$router.push({
        name: "Adventure Profile",
        params: { tour_id: item.id }
      });
    },

    getTours() {
      tourApi.getTours(this.includeArchives).then(response => {
        this.tours = response.data.tours;
      });
    },

    getSuppliers() {
      supplierApi.getAll().then(response => {
        this.suppliers = response.data;
      });
    },

    createTour() {
      tourApi
        .createTour(this.tour)
        .then(response => {
          this.$router.push(`/tour/${response.data.id}`);
        })
        .catch(err => {});
    },

    deleteTour(tourId) {
      Swal.fire({
        title: "Are you absolutely sure?",
        text: `This will delete the Adventure, along with all related data including booking info!`,
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-success",
        cancelButtonClass: "md-button md-danger",
        confirmButtonText: "Yes, delete it!",
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          tourApi.deleteTour(tourId).then(response => {
            this.tours = _filter(this.tours, function(tour) {
              return tour.id !== tourId;
            });

            this.$notify({
              timeout: 2500,
              message: "The Tour has been deleted.",
              icon: "cancel",
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "danger"
            });
          });
        }
      });
    },

    createDeparture() {
      tourApi
        .createDeparture(this.departure.tour, this.departure)
        .then(response => {
          this.$router.push(
            `/tour/${this.departure.tour.id}/departure/${response.data.id}`
          );
        });
    },

    newTour() {
      this.showTourDialog = true;
    },

    newDeparture(tour) {
      this.departure.tour = tour;
      this.showDepartureDialog = true;
    },

    cancelTour() {
      this.showTourDialog = false;
    },

    cancelDeparture() {
      this.departure.tour = null;
      this.showDepartureDialog = false;
    }
  },

  computed: {
    ...mapFields("tours", ["includeArchives"])
  },

  watch: {},

  mounted() {
    this.getTours();
    this.getSuppliers();
  }
};
</script>
<style lang="scss" scoped>
.md-switch {
  margin: 0;
}
.tour-list {
  .tour-type {
    text-transform: uppercase;
  }
  .tour-type-icon {
    margin-right: 5px;
  }

  .text-right {
    align-items: center;
  }
  h6 {
    margin: 0;
  }
  tr.archived {
    background-color: #b2000026;
  }
}
</style>
